import { KeywordResponse } from "../types";
import { ApiService } from "../../../config";
import { handleError } from "../../../utils/parseApiReturn";
import { AxiosRequestConfig } from "axios";

interface GetProfilesParams {
  profileStatus?: string;
  ranking?: string;
  localidade?: string;
  query?: string;
  page?: number;
  size?: number;
  sort?: string[];
  token: string;
}

export class ReportsService {
  /**
   * Recupera as palavras-chave de um perfil pelo publicId.
   * Endpoint público — não requer autenticação.
   *
   * @param profileId - publicId do BusinessProfile
   * @returns Array combinado de keywords e extraKeywords, com flag isExtra
   */
  static async getKeywords(profileId: string): Promise<KeywordResponse[]> {
    try {
      const response = await ApiService.get(`/keywords/profile/${profileId}`);
      const { keywords = [], extraKeywords = [] } = response.data.data;

      const extraWithFlag: KeywordResponse[] = extraKeywords.map(
        (kw: KeywordResponse) => ({ ...kw, isExtra: true })
      );

      return [...keywords, ...extraWithFlag];
    } catch (error) {
      console.error("Keywords API Error:", error);
      handleError(error, "Erro ao buscar palavras-chave.");
      return [];
    }
  }

  /**
   * Recupera lista paginada de perfis do usuário com suporte a busca, filtros e ordenação.
   * Útil para listar todos os perfis de um cliente de forma paginada.
   * 
   * @param params - Parâmetros de requisição incluindo autenticação, paginação e filtros
   * @param params.token - Token de autenticação Bearer
   * @param params.page - Número da página (padrão: 0)
   * @param params.size - Quantidade de itens por página (padrão: 20)
   * @param params.query - Termo de busca para filtrar perfis
   * @param params.profileStatus - Filtro por status do perfil
   * @param params.ranking - Filtro por ranking
   * @param params.localidade - Filtro por localidade
   * @returns Objeto com lista paginada de perfis e total de elementos
   */
  static async getMyProfiles(params: GetProfilesParams): Promise<any> {
    try {
      const { token, ...queryParams } = params;
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: queryParams.page ?? 0,
          size: queryParams.size ?? 20,
          ...(queryParams.query && { query: queryParams.query }),
          ...(queryParams.profileStatus && { profileStatus: queryParams.profileStatus }),
          ...(queryParams.ranking && { ranking: queryParams.ranking }),
          ...(queryParams.localidade && { localidade: queryParams.localidade }),
        },
      };

      const result = await ApiService.get("/customers/tracking", config);
      return result.data?.data || { content: [], totalElements: 0 };
    } catch (error) {
      handleError(error, "Erro ao buscar perfis.");
      return { content: [], totalElements: 0 };
    }
  }
}

